import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faServer,
  faNetworkWired,
  faDatabase,
  faGlobe,
  faToggleOn,
  faToggleOff,
  faPercent,
  faMapMarkerAlt,
  faMicrochip,
  faClock,
  faCheckCircle,
  faExclamationCircle,
  faTag,
  faChartLine,
  faDollarSign,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';

interface Discount {
  id: string;
  category: 'compute' | 'dataTransfer' | 'storage' | 'cloudfront';
  title: string;
  description: string;
  discountPercentage: number;
  terms: string[];
  enabled: boolean;
  usageBased?: boolean;
  currentCost?: number;
  actualMonthlySavings?: number;
  details: {
    type?: string;
    region?: string;
    architecture?: string;
    quantity?: number;
    capacity?: string;
    scope?: string;
    duration?: string;
    currentRate?: string;
    discountedRate?: string;
  };
}

export const PreApprovedDiscounts: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([
    // Compute Discounts
    {
      id: 'compute-1',
      category: 'compute',
      title: 'T4g.medium Mumbai Region - 30% Off',
      description: 'Get 30% discount on up to 5 t4g.medium instances in ap-south-1 (Mumbai)',
      discountPercentage: 30,
      terms: [
        'Limited to 5 instances',
        'Valid for ap-south-1 (Mumbai) region only',
        'ARM-based Graviton2 processors',
        'On-demand pricing with discount'
      ],
      enabled: false,
      details: {
        type: 't4g.medium',
        region: 'ap-south-1 (Mumbai)',
        architecture: 'ARM64 (Graviton2)',
        quantity: 5
      }
    },
    {
      id: 'compute-2',
      category: 'compute',
      title: 'Free Spot Instances - 3 Months',
      description: 'Get 10 free t3.small spot instances for development/testing workloads',
      discountPercentage: 100,
      terms: [
        'Valid for 3 months from activation',
        'Limited to 10 t3.small spot instances',
        'Subject to spot availability',
        'Best effort - may be interrupted'
      ],
      enabled: false,
      details: {
        type: 't3.small (Spot)',
        quantity: 10,
        duration: '3 months'
      }
    },
    {
      id: 'compute-3',
      category: 'compute',
      title: 'ARM/Graviton Instances - 25% Off',
      description: 'Migrate to ARM-based Graviton instances and save 25% on all sizes',
      discountPercentage: 25,
      terms: [
        'Applies to all Graviton instance types (t4g, m6g, c6g, r6g)',
        'All regions supported',
        'Stackable with existing RI/SP discounts',
        'No quantity limit'
      ],
      enabled: false,
      details: {
        architecture: 'ARM64 (Graviton2/3)',
        scope: 'All regions'
      }
    },
    {
      id: 'compute-4',
      category: 'compute',
      title: 'US-East-1 Instance Discount - 15% Off',
      description: 'Save 15% on all EC2 instance types in us-east-1 region',
      discountPercentage: 15,
      terms: [
        'Valid for us-east-1 (N. Virginia) only',
        'All instance families included',
        'Both on-demand and reserved instances',
        'Minimum 10 instances'
      ],
      enabled: false,
      details: {
        region: 'us-east-1 (N. Virginia)',
        scope: 'All instance types'
      }
    },

    // Data Transfer Discounts
    {
      id: 'datatransfer-1',
      category: 'dataTransfer',
      title: 'Global Data Transfer - 20% Off (5 TB)',
      description: 'Get 20% discount on first 5 TB of data transfer out to internet',
      discountPercentage: 20,
      terms: [
        'Applies to first 5 TB per month',
        'All regions included',
        'Internet egress only',
        'Standard AWS rates after 5 TB'
      ],
      enabled: false,
      details: {
        capacity: '5 TB/month',
        scope: 'Global (All regions)'
      }
    },
    {
      id: 'datatransfer-2',
      category: 'dataTransfer',
      title: 'Asia-Pacific Data Transfer - 25% Off',
      description: 'Save 25% on data transfer within APAC regions (up to 10 TB)',
      discountPercentage: 25,
      terms: [
        'Valid for inter-region transfer within APAC',
        'Up to 10 TB per month',
        'Includes ap-south-1, ap-southeast-1, ap-northeast-1',
        'Does not apply to internet egress'
      ],
      enabled: false,
      details: {
        capacity: '10 TB/month',
        scope: 'Asia-Pacific regions'
      }
    },
    {
      id: 'datatransfer-3',
      category: 'dataTransfer',
      title: 'CloudFront Integration Discount - 30% Off',
      description: 'Get 30% off data transfer when using CloudFront for content delivery',
      discountPercentage: 30,
      terms: [
        'Requires active CloudFront distribution',
        'Applies to origin fetch from S3/EC2',
        'Up to 20 TB per month',
        'All edge locations included'
      ],
      enabled: false,
      details: {
        capacity: '20 TB/month',
        scope: 'CloudFront to origin'
      }
    },

    // Storage Discounts
    {
      id: 'storage-1',
      category: 'storage',
      title: 'S3 Standard Storage - 15% Off (50 TB)',
      description: 'Save 15% on first 50 TB of S3 Standard storage',
      discountPercentage: 15,
      terms: [
        'S3 Standard storage class only',
        'First 50 TB per month',
        'All regions included',
        'Does not apply to requests/retrievals'
      ],
      enabled: false,
      details: {
        type: 'S3 Standard',
        capacity: '50 TB',
        scope: 'All regions'
      }
    },
    {
      id: 'storage-2',
      category: 'storage',
      title: 'EBS gp3 Volumes - 20% Off',
      description: 'Get 20% discount on gp3 volumes (up to 100 TB)',
      discountPercentage: 20,
      terms: [
        'gp3 volumes only (baseline 3000 IOPS)',
        'Up to 100 TB provisioned',
        'All regions supported',
        'Additional IOPS/throughput at standard rates'
      ],
      enabled: false,
      details: {
        type: 'EBS gp3',
        capacity: '100 TB',
        scope: 'All regions'
      }
    },
    {
      id: 'storage-3',
      category: 'storage',
      title: 'S3 Intelligent-Tiering - Free Monitoring',
      description: 'Free monitoring and automation fees for S3 Intelligent-Tiering (12 months)',
      discountPercentage: 100,
      terms: [
        'Waives monitoring and automation fees',
        'Valid for 12 months',
        'Storage costs apply at standard rates',
        'Unlimited storage capacity'
      ],
      enabled: false,
      details: {
        type: 'S3 Intelligent-Tiering',
        duration: '12 months',
        scope: 'Monitoring fees only'
      }
    },

    // CloudFront Discounts
    {
      id: 'cloudfront-1',
      category: 'cloudfront',
      title: 'CloudFront Data Transfer - 25% Off',
      description: 'Save 25% on CloudFront data transfer out to internet (first 10 TB)',
      discountPercentage: 25,
      terms: [
        'First 10 TB per month',
        'All edge locations included',
        'HTTP and HTTPS requests',
        'Does not apply to regional data transfer out'
      ],
      enabled: false,
      details: {
        capacity: '10 TB/month',
        scope: 'All edge locations'
      }
    },
    {
      id: 'cloudfront-2',
      category: 'cloudfront',
      title: 'CloudFront + S3 Bundle - 30% Off',
      description: 'Get 30% discount when using CloudFront with S3 origin',
      discountPercentage: 30,
      terms: [
        'Requires S3 as origin',
        'Applies to both CloudFront and S3 transfer',
        'Up to 15 TB per month',
        'Origin Shield at standard rates'
      ],
      enabled: false,
      details: {
        capacity: '15 TB/month',
        scope: 'CloudFront + S3 combined'
      }
    },

    // Usage-Based Discounts (Generated from scan data)
    {
      id: 'usage-compute-1',
      category: 'compute',
      title: 'g4dn.xlarge Mumbai - 25% Off (Usage-Based)',
      description: 'You have 10 g4dn.xlarge running in ap-south-1. 3 instances eligible for 25% discount for 1 month',
      discountPercentage: 25,
      usageBased: true,
      currentCost: 788,
      actualMonthlySavings: 197,
      terms: [
        'Based on your current usage pattern',
        'Applies to 3 out of 10 g4dn.xlarge instances',
        'Valid for 1 month from activation',
        'Discount applies to ap-south-1 (Mumbai) region only'
      ],
      enabled: false,
      details: {
        type: 'g4dn.xlarge',
        region: 'ap-south-1 (Mumbai)',
        quantity: 3,
        currentRate: '$0.73/hr',
        discountedRate: '$0.55/hr'
      }
    },
    {
      id: 'usage-datatransfer-1',
      category: 'dataTransfer',
      title: 'US-East-1 Data Transfer - 33% Off (Usage-Based)',
      description: 'You transfer 50 TB/mo from us-east-1 at $0.09/GB. 10 TB eligible for discount at $0.06/GB',
      discountPercentage: 33,
      usageBased: true,
      currentCost: 4608,
      actualMonthlySavings: 307,
      terms: [
        'Based on last 3 months average usage',
        'Applies to 10 TB out of 50 TB monthly transfer',
        'us-east-1 internet egress only',
        'Valid for 1 month, renewable'
      ],
      enabled: false,
      details: {
        region: 'us-east-1 (N. Virginia)',
        capacity: '10 TB/month',
        currentRate: '$0.09/GB',
        discountedRate: '$0.06/GB'
      }
    },
    {
      id: 'usage-compute-2',
      category: 'compute',
      title: 'm5.2xlarge US-West-2 - 20% Off (Usage-Based)',
      description: 'You have 15 m5.2xlarge in us-west-2. 5 instances eligible for 20% discount',
      discountPercentage: 20,
      usageBased: true,
      currentCost: 1920,
      actualMonthlySavings: 384,
      terms: [
        'Based on 24/7 utilization pattern',
        '5 out of 15 m5.2xlarge instances eligible',
        'Valid for us-west-2 (Oregon) only',
        'Discount valid for 1 month'
      ],
      enabled: false,
      details: {
        type: 'm5.2xlarge',
        region: 'us-west-2 (Oregon)',
        quantity: 5,
        currentRate: '$0.384/hr',
        discountedRate: '$0.307/hr'
      }
    }
  ]);

  const [collapsedSections, setCollapsedSections] = useState<{
    compute: boolean;
    dataTransfer: boolean;
    storage: boolean;
    cloudfront: boolean;
  }>({
    compute: false,
    dataTransfer: false,
    storage: false,
    cloudfront: false
  });

  const toggleSection = (section: 'compute' | 'dataTransfer' | 'storage' | 'cloudfront') => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleDiscount = (id: string) => {
    setDiscounts(prev =>
      prev.map(discount =>
        discount.id === id
          ? { ...discount, enabled: !discount.enabled }
          : discount
      )
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'compute': return faServer;
      case 'dataTransfer': return faNetworkWired;
      case 'storage': return faDatabase;
      case 'cloudfront': return faGlobe;
      default: return faTag;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'compute': return 'orange';
      case 'dataTransfer': return 'blue';
      case 'storage': return 'purple';
      case 'cloudfront': return 'green';
      default: return 'gray';
    }
  };

  const categorizedDiscounts = {
    compute: discounts.filter(d => d.category === 'compute'),
    dataTransfer: discounts.filter(d => d.category === 'dataTransfer'),
    storage: discounts.filter(d => d.category === 'storage'),
    cloudfront: discounts.filter(d => d.category === 'cloudfront')
  };

  const enabledCount = discounts.filter(d => d.enabled).length;
  const totalPotentialSavings = discounts
    .filter(d => d.enabled)
    .reduce((sum, d) => {
      // For usage-based discounts, use actual monthly savings
      if (d.usageBased && d.actualMonthlySavings) {
        return sum + d.actualMonthlySavings;
      }
      // For generic discounts, estimate
      return sum + (d.discountPercentage === 100 ? 50 : d.discountPercentage * 10);
    }, 0);

  const savingsAchieved = discounts
    .filter(d => d.enabled)
    .reduce((sum, d) => {
      if (d.usageBased && d.actualMonthlySavings) {
        return sum + d.actualMonthlySavings;
      }
      return sum;
    }, 0);

  return (
    <div className="space-y-3 p-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <h2 className="text-xs font-semibold text-gray-900 mb-0.5 flex items-center">
              <FontAwesomeIcon icon={faTag} className="text-green-600 mr-1.5 text-xs" />
              Pre-approved Discounts
            </h2>
            <p className="text-xs text-gray-600">
              Exclusive DevOpsX discounts - enable instantly with a toggle.
            </p>
          </div>

          {/* Summary Stats - Inline */}
          <div className="flex items-center gap-2">
            <div className="bg-white rounded px-2 py-1 border border-green-200">
              <div className="text-[10px] text-gray-600">Available</div>
              <div className="text-xs font-bold text-gray-900">{discounts.length}</div>
            </div>
            <div className="bg-white rounded px-2 py-1 border border-blue-200">
              <div className="text-[10px] text-gray-600">Enabled</div>
              <div className="text-xs font-bold text-blue-600">{enabledCount}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Compute Discounts */}
      <div>
        <h3
          className="text-xs font-semibold text-gray-900 mb-2 flex items-center cursor-pointer hover:text-orange-700 transition-colors"
          onClick={() => toggleSection('compute')}
        >
          <FontAwesomeIcon icon={faServer} className="text-orange-600 mr-2 text-xs" />
          Compute Discounts ({categorizedDiscounts.compute.length})
          <FontAwesomeIcon
            icon={collapsedSections.compute ? faChevronDown : faChevronUp}
            className="ml-2 text-xs text-gray-500"
          />
        </h3>
        {!collapsedSections.compute && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categorizedDiscounts.compute.map(discount => (
              <DiscountCard
                key={discount.id}
                discount={discount}
                onToggle={toggleDiscount}
                categoryColor={getCategoryColor(discount.category)}
                categoryIcon={getCategoryIcon(discount.category)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Data Transfer Discounts */}
      <div>
        <h3
          className="text-xs font-semibold text-gray-900 mb-2 flex items-center cursor-pointer hover:text-blue-700 transition-colors"
          onClick={() => toggleSection('dataTransfer')}
        >
          <FontAwesomeIcon icon={faNetworkWired} className="text-blue-600 mr-2 text-xs" />
          Data Transfer Discounts ({categorizedDiscounts.dataTransfer.length})
          <FontAwesomeIcon
            icon={collapsedSections.dataTransfer ? faChevronDown : faChevronUp}
            className="ml-2 text-xs text-gray-500"
          />
        </h3>
        {!collapsedSections.dataTransfer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categorizedDiscounts.dataTransfer.map(discount => (
              <DiscountCard
                key={discount.id}
                discount={discount}
                onToggle={toggleDiscount}
                categoryColor={getCategoryColor(discount.category)}
                categoryIcon={getCategoryIcon(discount.category)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Storage Discounts */}
      <div>
        <h3
          className="text-xs font-semibold text-gray-900 mb-2 flex items-center cursor-pointer hover:text-purple-700 transition-colors"
          onClick={() => toggleSection('storage')}
        >
          <FontAwesomeIcon icon={faDatabase} className="text-purple-600 mr-2 text-xs" />
          Storage Discounts ({categorizedDiscounts.storage.length})
          <FontAwesomeIcon
            icon={collapsedSections.storage ? faChevronDown : faChevronUp}
            className="ml-2 text-xs text-gray-500"
          />
        </h3>
        {!collapsedSections.storage && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categorizedDiscounts.storage.map(discount => (
              <DiscountCard
                key={discount.id}
                discount={discount}
                onToggle={toggleDiscount}
                categoryColor={getCategoryColor(discount.category)}
                categoryIcon={getCategoryIcon(discount.category)}
              />
            ))}
          </div>
        )}
      </div>

      {/* CloudFront Discounts */}
      <div>
        <h3
          className="text-xs font-semibold text-gray-900 mb-2 flex items-center cursor-pointer hover:text-green-700 transition-colors"
          onClick={() => toggleSection('cloudfront')}
        >
          <FontAwesomeIcon icon={faGlobe} className="text-green-600 mr-2 text-xs" />
          CloudFront Discounts ({categorizedDiscounts.cloudfront.length})
          <FontAwesomeIcon
            icon={collapsedSections.cloudfront ? faChevronDown : faChevronUp}
            className="ml-2 text-xs text-gray-500"
          />
        </h3>
        {!collapsedSections.cloudfront && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categorizedDiscounts.cloudfront.map(discount => (
              <DiscountCard
                key={discount.id}
                discount={discount}
                onToggle={toggleDiscount}
                categoryColor={getCategoryColor(discount.category)}
                categoryIcon={getCategoryIcon(discount.category)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface DiscountCardProps {
  discount: Discount;
  onToggle: (id: string) => void;
  categoryColor: string;
  categoryIcon: any;
}

const DiscountCard: React.FC<DiscountCardProps> = ({ discount, onToggle, categoryColor, categoryIcon }) => {
  const colorClasses = {
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      icon: 'text-orange-600',
      badge: 'bg-orange-100 text-orange-800 border-orange-300'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-800 border-blue-300'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'text-purple-600',
      badge: 'bg-purple-100 text-purple-800 border-purple-300'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-600',
      badge: 'bg-green-100 text-green-800 border-green-300'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      icon: 'text-gray-600',
      badge: 'bg-gray-100 text-gray-800 border-gray-300'
    }
  };

  const colors = colorClasses[categoryColor as keyof typeof colorClasses] || colorClasses.gray;

  return (
    <div className={`${discount.enabled ? 'bg-white border-2 border-green-500 shadow-lg' : 'bg-white border'} border-gray-200 rounded-lg p-4 transition-all hover:shadow-md`}>
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center`}>
              <FontAwesomeIcon icon={categoryIcon} className={`${colors.icon} text-xs`} />
            </div>
            <div className="flex-1">
              <h4 className="text-xs font-semibold text-gray-900 mb-1">{discount.title}</h4>
              <p className="text-xs text-gray-600">{discount.description}</p>
            </div>
          </div>

          {/* Toggle */}
          <button
            onClick={() => onToggle(discount.id)}
            className={`ml-2 transition-colors ${discount.enabled ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <FontAwesomeIcon
              icon={discount.enabled ? faToggleOn : faToggleOff}
              className="text-3xl"
            />
          </button>
        </div>

        {/* Discount Badge */}
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${colors.badge}`}>
            <FontAwesomeIcon icon={faPercent} className="mr-1" />
            {discount.discountPercentage}% OFF
          </span>
          {discount.enabled && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
              ACTIVE
            </span>
          )}
        </div>

        {/* Details Grid */}
        <div className={`${colors.bg} rounded-lg p-3 text-xs space-y-1`}>
          {discount.usageBased && discount.currentCost && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faDollarSign} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Current Cost:</strong> ${discount.currentCost.toLocaleString()}/mo</span>
            </div>
          )}
          {discount.usageBased && discount.actualMonthlySavings && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faDollarSign} className={`text-green-600 mr-2 w-4`} />
              <span className="text-gray-700"><strong>Monthly Savings:</strong> <span className="text-green-700 font-bold">${discount.actualMonthlySavings.toLocaleString()}/mo</span></span>
            </div>
          )}
          {discount.details.currentRate && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faPercent} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Current Rate:</strong> {discount.details.currentRate}</span>
            </div>
          )}
          {discount.details.discountedRate && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faPercent} className={`text-green-600 mr-2 w-4`} />
              <span className="text-gray-700"><strong>Discounted Rate:</strong> <span className="text-green-700">{discount.details.discountedRate}</span></span>
            </div>
          )}
          {discount.details.type && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faMicrochip} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Type:</strong> {discount.details.type}</span>
            </div>
          )}
          {discount.details.region && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faMapMarkerAlt} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Region:</strong> {discount.details.region}</span>
            </div>
          )}
          {discount.details.architecture && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faMicrochip} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Architecture:</strong> {discount.details.architecture}</span>
            </div>
          )}
          {discount.details.quantity && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faServer} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Quantity:</strong> {discount.details.quantity}</span>
            </div>
          )}
          {discount.details.capacity && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faDatabase} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Capacity:</strong> {discount.details.capacity}</span>
            </div>
          )}
          {discount.details.scope && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faGlobe} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Scope:</strong> {discount.details.scope}</span>
            </div>
          )}
          {discount.details.duration && (
            <div className="flex items-center">
              <FontAwesomeIcon icon={faClock} className={`${colors.icon} mr-2 w-4`} />
              <span className="text-gray-700"><strong>Duration:</strong> {discount.details.duration}</span>
            </div>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="border-t border-gray-200 pt-3">
          <div className="text-xs text-gray-600 mb-2 flex items-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
            <strong>Terms:</strong>
          </div>
          <ul className="text-xs text-gray-600 space-y-1 ml-4">
            {discount.terms.map((term, idx) => (
              <li key={idx} className="list-disc">{term}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
